/**
 * Utility functions for formatting text and dates
 */

/**
 * Truncates text to a specified length and adds ellipsis
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text with ellipsis if needed
 */
export function truncateText(text, maxLength = 100) {
  if (!text) return '';
  
  if (text.length <= maxLength) {
    return text;
  }
  
  // Find the last space before maxLength to avoid cutting words
  const lastSpace = text.lastIndexOf(' ', maxLength);
  const breakPoint = lastSpace > maxLength / 2 ? lastSpace : maxLength;
  
  return text.substring(0, breakPoint) + '...';
}

/**
 * Formats a date string into a user-friendly format
 * @param {string} dateString - The ISO date string
 * @returns {string} - Formatted date string
 */
export function formatDate(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Format options
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return date.toLocaleDateString(undefined, options);
  } catch (error) {
    console.error('[formatters] Error formatting date:', error);
    return '';
  }
}

/**
 * Formats the time elapsed since a given date
 * @param {string} dateString - The ISO date string
 * @returns {string} - Relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    }
    
    // For older dates, use the standard format
    return formatDate(dateString);
  } catch (error) {
    console.error('[formatters] Error calculating relative time:', error);
    return '';
  }
}

/**
 * Capitalizes the first letter of each word in a string
 * @param {string} text - The text to format
 * @returns {string} - Formatted text with capitalized words
 */
export function capitalizeWords(text) {
  if (!text) return '';
  
  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Formats age range for display
 * @param {string} ageRange - Raw age range string (e.g., "6-12 years")
 * @returns {string} - Formatted age range
 */
export function formatAgeRange(ageRange) {
  if (!ageRange) return '';
  
  // Already formatted correctly
  if (ageRange.includes('years')) {
    return ageRange;
  }
  
  // Add "years" if missing
  return `${ageRange} years`;
}
